/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useMemo} from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import { OfferStatus } from 'types/enums/OfferStatus'
import { getOfferStatusOptions } from 'types/enums/OfferStatus.meta'

interface UiOfferStatusSelectProps {
    value: OfferStatus | null
    onChange: (value: OfferStatus | null) => void
    disabled?: boolean
    dropdownDirection?: 'up' | 'down'
    className?: string
}

const UiOfferStatusSelect: React.FC<UiOfferStatusSelectProps> = ({
    value,
    onChange,
    disabled,
    dropdownDirection,
    className,
}) => {
    const { t } = useTranslation()
    const options = useMemo(() => {
        const allOptions = getOfferStatusOptions(t)

        if (value === OfferStatus.Accepted) {
            return allOptions
        }

        return allOptions.filter((option) => option.value !== OfferStatus.Accepted)
    }, [t, value])

    return (
        <div className={clsx('UiOfferStatusSelect', className)}>
            <UiSelectWithGraphics
                options={options}
                selected={value}
                setSelected={(nextValue) => {
                    if (nextValue === OfferStatus.Accepted) {
                        return
                    }
                    onChange((nextValue as OfferStatus) ?? null)
                }}
                label={t('Offer status')}
                disabled={disabled}
                dropdownDirection={dropdownDirection}
            />
        </div>
    )
}

export default UiOfferStatusSelect
export * from 'types/enums/OfferStatus.meta'
